import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, getToken, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { ShareButton } from "@/components/ShareButton";
import { PAGE, BTN_PRIMARY, BTN_GHOST, ICON_BTN, CARD, INPUT } from "@/lib/styles";
import { FolderPlus, Folder, Upload, FileText, Trash2, Download, X, Files, FilePlus, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

function fmtSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function Documents() {
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(""); // "" = sin carpeta (raíz)
  const [creating, setCreating] = useState(false);
  const [newFolder, setNewFolder] = useState("");
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null); // {id, filename}
  const [dragOver, setDragOver] = useState(false);
  const [pdfModal, setPdfModal] = useState(false);
  const [pdfTitle, setPdfTitle] = useState("");
  const [pdfContent, setPdfContent] = useState("");
  const [pdfSaving, setPdfSaving] = useState(false);
  const fileRef = useRef(null);

  const loadFolders = useCallback(async () => {
    try {
      const { data } = await api.get("/docs/folders");
      setFolders(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  const loadFiles = useCallback(async (folderId) => {
    try {
      const { data } = await api.get("/docs/files", { params: { folder_id: folderId } });
      setFiles(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { loadFiles(selected); }, [selected, loadFiles]);

  const createFolder = async () => {
    if (!newFolder.trim()) return;
    try {
      await api.post("/docs/folders", { name: newFolder.trim() });
      setNewFolder("");
      setCreating(false);
      loadFolders();
      toast.success("Carpeta creada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteFolder = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar esta carpeta y sus documentos?")) return;
    try {
      await api.delete(`/docs/folders/${id}`);
      if (selected === id) setSelected("");
      loadFolders();
      loadFiles(selected === id ? "" : selected);
      toast.success("Carpeta eliminada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of arr) {
      const form = new FormData();
      form.append("file", f);
      form.append("folder_id", selected);
      try {
        await api.post("/docs/files", form, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err.response?.data?.detail)}`);
      }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} documento(s) subido(s)`);
    loadFiles(selected);
    loadFolders();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const removeFile = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/docs/files/${id}`);
      setFiles((fs) => fs.filter((x) => x.id !== id));
      loadFolders();
      toast.success("Eliminado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const fileUrl = (id) => `${API}/docs/files/${id}/download?auth=${getToken()}`;

  const createPdf = async () => {
    if (!pdfTitle.trim()) { toast.info("Escribe un título para el PDF"); return; }
    setPdfSaving(true);
    try {
      await api.post("/docs/create-pdf", { title: pdfTitle.trim(), content: pdfContent, folder_id: selected });
      setPdfModal(false);
      setPdfTitle("");
      setPdfContent("");
      loadFiles(selected);
      loadFolders();
      toast.success("PDF creado y guardado en Documentos");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPdfSaving(false);
    }
  };

  const download = (e, id, filename) => {
    e.stopPropagation();
    const a = document.createElement("a");
    a.href = fileUrl(id);
    a.download = filename;
    a.click();
  };

  return (
    <div className={`${PAGE} font-body`}>
      <Navbar />
      <div className="max-w-6xl mx-auto p-6 md:p-10">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tight">Documentos</h1>
            <p className="font-mono2 text-xs text-slate-500 dark:text-slate-400 mt-2">Sube, organiza y lee tus PDFs</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setPdfModal(true); }} className={BTN_GHOST} data-testid="create-pdf-button">
              <FilePlus size={16} /> Crear PDF
            </button>
            <button onClick={() => fileRef.current?.click()} className={BTN_PRIMARY} disabled={uploading} data-testid="upload-button">
              <Upload size={16} /> {uploading ? "Subiendo..." : "Subir PDF"}
            </button>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="upload-input" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6">
          {/* Folders sidebar */}
          <aside className="flex flex-col gap-2">
            <button
              onClick={() => setSelected("")}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${selected === "" ? "bg-indigo-600 text-white" : "hover:bg-slate-100 dark:hover:bg-slate-800"}`}
              data-testid="folder-root"
            >
              <Files size={16} /> Todos los documentos
            </button>
            {folders.map((f) => (
              <button
                key={f.id}
                onClick={() => setSelected(f.id)}
                className={`group flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${selected === f.id ? "bg-indigo-600 text-white" : "hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                data-testid={`folder-${f.id}`}
              >
                <Folder size={16} /> <span className="truncate flex-1">{f.name}</span>
                <span className="font-mono2 text-[10px] opacity-70">{f.count}</span>
                <span onClick={(e) => deleteFolder(e, f.id)} className="opacity-0 group-hover:opacity-100 hover:text-rose-400" data-testid={`delete-folder-${f.id}`}>
                  <Trash2 size={13} />
                </span>
              </button>
            ))}
            {creating ? (
              <div className="flex gap-1 mt-1">
                <input autoFocus className={`${INPUT} py-1.5 text-sm`} placeholder="Nombre..." value={newFolder} onChange={(e) => setNewFolder(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()} data-testid="new-folder-input" />
                <button onClick={createFolder} className={ICON_BTN} data-testid="confirm-folder">✓</button>
              </div>
            ) : (
              <button onClick={() => setCreating(true)} className={`${BTN_GHOST} mt-1 justify-start`} data-testid="new-folder-button">
                <FolderPlus size={16} /> Nueva carpeta
              </button>
            )}
          </aside>

          {/* Files area */}
          <main
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`min-h-[50vh] rounded-xl transition-colors ${dragOver ? "ring-2 ring-indigo-500 bg-indigo-500/5" : ""}`}
            data-testid="files-dropzone"
          >
            {selected !== "" && (
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-normal text-lg flex items-center gap-2">
                  <Folder size={18} className="text-indigo-500" />
                  {folders.find((f) => f.id === selected)?.name || "Carpeta"}
                </h2>
                <ShareButton targetType="folder" targetId={selected} iconOnly={false} label="Compartir carpeta" />
              </div>
            )}
            {files.length === 0 ? (
              <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-16 text-center h-full flex flex-col items-center justify-center">
                <FileText size={40} className="text-slate-300 dark:text-slate-600 mb-4" />
                <h3 className="font-heading font-normal text-lg mb-1">Sin documentos aquí</h3>
                <p className="font-body text-sm text-slate-500 dark:text-slate-400">Arrastra un PDF aquí o pulsa "Subir PDF".</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="files-grid">
                {files.map((d) => (
                  <div key={d.id} className={`${CARD} p-4 cursor-pointer group flex flex-col gap-3`} onClick={() => setViewer(d)} data-testid={`file-card-${d.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-lg bg-rose-500/10 text-rose-500 flex items-center justify-center"><FileText size={20} /></div>
                        <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-500 border border-rose-500/20" data-testid={`file-type-${d.id}`}>PDF</span>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
                        <ShareButton targetType="document" targetId={d.id} />
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/editar-pdf/${d.id}`); }} className={ICON_BTN} title="Editar PDF" data-testid={`edit-${d.id}`}><Pencil size={14} /></button>
                        <button onClick={(e) => download(e, d.id, d.filename)} className={ICON_BTN} data-testid={`download-${d.id}`}><Download size={14} /></button>
                        <button onClick={(e) => removeFile(e, d.id)} className={ICON_BTN} data-testid={`delete-file-${d.id}`}><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <h3 className="font-heading font-medium text-sm break-words leading-snug">{d.filename}</h3>
                    <p className="font-mono2 text-[10px] text-slate-400 mt-auto">Documento PDF · {fmtSize(d.size)} · {d.created_at ? format(new Date(d.created_at), "d 'de' MMMM yyyy", { locale: es }) : ""}</p>
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex flex-col p-4 md:p-8" data-testid="pdf-viewer">
          <div className="flex items-center justify-between mb-3 text-white">
            <h3 className="font-heading font-medium text-sm truncate">{viewer.filename}</h3>
            <div className="flex gap-2">
              <button onClick={() => { setViewer(null); navigate(`/editar-pdf/${viewer.id}`); }} className="border border-white/30 rounded-lg p-2 hover:bg-white/10" title="Editar" data-testid="viewer-edit"><Pencil size={18} /></button>
              <button onClick={(e) => download(e, viewer.id, viewer.filename)} className="border border-white/30 rounded-lg p-2 hover:bg-white/10" data-testid="viewer-download"><Download size={18} /></button>
              <button onClick={() => setViewer(null)} className="border border-white/30 rounded-lg p-2 hover:bg-white/10" data-testid="viewer-close"><X size={18} /></button>
            </div>
          </div>
          <iframe title={viewer.filename} src={fileUrl(viewer.id)} className="flex-1 w-full rounded-lg bg-white" data-testid="viewer-iframe" />
        </div>
      )}
      {pdfModal && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !pdfSaving && setPdfModal(false)} data-testid="create-pdf-modal">
          <div className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-medium text-lg flex items-center gap-2"><FilePlus size={19} className="text-indigo-500" /> Crear PDF</h3>
              <button onClick={() => setPdfModal(false)} className={ICON_BTN} data-testid="create-pdf-close"><X size={16} /></button>
            </div>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-4">
              Escribe el contenido y lo guardamos como PDF en {selected ? "esta carpeta" : "Documentos"}. Puedes usar <span className="font-mono2">#</span> para títulos y <span className="font-mono2">-</span> para listas.
            </p>
            <input className={`${INPUT} mb-3`} placeholder="Título del documento" value={pdfTitle} onChange={(e) => setPdfTitle(e.target.value)} data-testid="create-pdf-title" />
            <textarea className={`${INPUT} h-56 resize-none font-mono2 text-sm leading-relaxed`} placeholder={"# Mi documento\n\nEscribe aquí el contenido...\n\n- Punto uno\n- Punto dos"} value={pdfContent} onChange={(e) => setPdfContent(e.target.value)} data-testid="create-pdf-content" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPdfModal(false)} className={BTN_GHOST} data-testid="create-pdf-cancel">Cancelar</button>
              <button onClick={createPdf} className={BTN_PRIMARY} disabled={pdfSaving} data-testid="create-pdf-save">
                {pdfSaving ? <Loader2 size={16} className="animate-spin" /> : <FilePlus size={16} />} Generar PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
